import React, { useState, useEffect } from "react";
import { Globe, Monitor, ExternalLink } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle, Switch,
} from "../ui";
import { useBranding } from "../../contexts/BrandingContext";
import { baseHost } from "../../utils/brandHost";


const CmsWebsiteSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  // useBranding() spreads branding fields at the top level, not under `.branding`.
  const branding = useBranding();

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await apiClient.get("/institute/cms-settings");
      setEnabled(!!data.cmsEnabled);
    } catch {
      showToast("Failed to load CMS website settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (value) => {
    setSaving(true);
    try {
      const { data } = await apiClient.patch("/institute/cms-settings", {
        cmsEnabled: value,
      });
      setEnabled(!!data.cmsEnabled);
      showToast(
        data.cmsEnabled
          ? "Public website is now enabled"
          : "Public website has been disabled",
        "success"
      );
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update setting", "error");
    } finally {
      setSaving(false);
    }
  };

  // Build the public website URL from branding context (subdomain / custom domain)
  const siteUrl = branding?.subdomain
    ? `https://${baseHost(branding.subdomain)}`
    : null;

  if (loading) {
    return (
      <Shimmer height={132} count={1} />
    );
  }

  return (
    <div className="cms-website-settings">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Globe size={18} /> Public Marketing Website
          </AppCardTitle>
          <p className="settings-card-desc">
            When enabled, prospective students can visit your public marketing site
            to browse courses, read blogs, and submit enquiries — without logging in.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <Switch
            label={<><Monitor size={15} aria-hidden="true" /> Enable public website</>}
            checked={enabled}
            onChange={handleToggle}
            disabled={saving}
          />

          {enabled && siteUrl && (
            <div className="cms-website-settings__url-row">
              <span className="cms-website-settings__url-label">Your public site URL:</span>
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cms-website-settings__url-link"
              >
                {siteUrl}
                <ExternalLink size={13} className="cms-website-settings__url-icon" />
              </a>
            </div>
          )}

          {!enabled && (
            <p className="cms-website-settings__hint">
              Turn on the public website to let prospective students discover your courses.
              You can turn it off at any time without losing any data.
            </p>
          )}
        </AppCardContent>
      </AppCard>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default CmsWebsiteSettings;
